import { httpApi } from '@app/api/http.api';
import { ReportFilter, ReportFormat, ReportExportFile, downloadReport } from '@app/api/reportDownload';

/**
 * What one user cost, for the selected range.
 *
 * `costUsd` is nullable and means UNKNOWN, never zero: the image model does not return
 * token usage on every model, and a model nobody has priced yet cannot be costed. A zero
 * would read as "this user was free", which is a different — and false — claim.
 */
export interface CostReportRow {
  userId: number;
  userName: string | null;
  /** Distinct tutorials this user's recorded calls belong to. */
  generations: number;
  /** Individual OpenAI calls, retries included — retries cost money too. */
  calls: number;
  costUsd: number | null;
  /** How many of `calls` carried no price. The caveat printed beside the cost. */
  unknownCalls: number;
  /** Coins consumed, already net of refunds. */
  coinsSpent: number;
  /** Coins added by purchase, bonus or a positive adjustment. */
  coinsToppedUp: number;
}

export interface CostReportTotals {
  users: number;
  generations: number;
  calls: number;
  costUsd: number | null;
  unknownCalls: number;
  coinsSpent: number;
  coinsToppedUp: number;
}

/**
 * What OpenAI itself reports for the organisation.
 *
 * Deliberately NOT merged into our totals: ours covers ai-draw only and misses anything
 * not instrumented, theirs covers the whole organisation including other consumers. The
 * gap between them is the number worth looking at.
 *
 * There is no balance and no headroom here. OpenAI exposes no endpoint for a remaining
 * prepaid balance, and the only spend-limit endpoint is a POST that SETS the limit — a
 * report has no business writing an org-wide billing setting to find out what it is.
 */
export type CostReportOrg =
  | { available: false; reason: string }
  | { available: true; spendUsd: number | null; currency: string; partial: boolean };

export interface CostReportResponse {
  rows: CostReportRow[];
  totals: CostReportTotals;
  org: CostReportOrg;
}

export type CostReportFilter = ReportFilter;
export type CostReportFormat = ReportFormat;
export type CostReportExportFile = ReportExportFile;

export const getCostReport = (params: CostReportFilter = {}): Promise<CostReportResponse> =>
  httpApi.get<CostReportResponse>('ai-draw/admin/cost-report', { params }).then(({ data }) => data);

export const exportCostReport = (
  params: CostReportFilter = {},
  format: CostReportFormat = 'xlsx',
): Promise<CostReportExportFile> => downloadReport('ai-draw/admin/cost-report/export', params, format);
